import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { IPaymentsAndDeductions } from 'API/paymentAndDeductions';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { DataItem } from './DataItem';
import { GroupStatus } from './GroupStatus';
import { BENEFITS_GROUP_STATUS } from 'Enum/paymentsDeduction';
import { LinkedAppliedNumbers } from './LinkedAppliedNumbers';
import { AccordionArrowPurple, AccordionArrowPurpleClose } from 'Images';
import { useMemo, useState } from 'react';
import { TextWithIcon } from 'Components/TextWithIcon';
import { format } from 'date-fns';
import { shorterDateFormat } from 'Config';
import { getPaymentDeductionType } from 'Utils/getPaymentStatus';
import { Link } from 'react-router-dom';

interface IPaymentDeduction {
  paymentDeductionItem: IPaymentsAndDeductions;
}

export const PaymentDeduction = ({
  paymentDeductionItem,
}: IPaymentDeduction) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.PaymentsDeductions
  ).paymentDeductionCard;
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const isDesktop = useIsDesktop();

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    expanded: boolean
  ) => {
    setAccordionExpanded(expanded);
  };

  const fullRequestNumber = useMemo(
    () => String(paymentDeductionItem.requestNumber).replace(/-/g, '000'),
    [paymentDeductionItem.requestNumber]
  );

  const isDeduction =
    paymentDeductionItem.benefitGroupCode === BENEFITS_GROUP_STATUS.DEDUCTION;

  return (
    <Accordion
      expanded={accordionExpanded}
      aria-expanded={accordionExpanded}
      aria-controls={`paymentDeduction-content-${paymentDeductionItem.requestNumber}`}
      onChange={handleAccordionChange}
      disableGutters
      elevation={0}
      sx={{
        bgcolor: '#fff',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'primary.shade_100',
        boxShadow: '0px 1px 12px 0px rgba(8, 30, 84, 0.04)',
        position: 'relative',
        paddingBottom: '0',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        sx={{
          gap: isDesktop ? '0' : '22px',
          alignItems: isDesktop ? undefined : 'flex-start',
          padding: isDesktop ? '32px 24px' : '24px 16px',
          '.MuiAccordionSummary-content': {
            margin: 0,
            width: isDesktop ? undefined : '100%',
          },
          '.MuiAccordionSummary-expandIconWrapper': {
            position: 'absolute',
            right: isDesktop ? '32px' : '10px',
            top: isDesktop ? '32px' : '24px',
          },
        }}
        expandIcon={
          <>
            {!accordionExpanded && (
              <Avatar
                src={AccordionArrowPurple}
                alt={''}
                sx={{
                  width: '40px',
                  height: '40px',
                }}
              />
            )}
            {accordionExpanded && (
              <Avatar
                src={AccordionArrowPurpleClose}
                alt={''}
                sx={{
                  width: '35px',
                  height: '35px',
                  rotate: '180deg',
                }}
              />
            )}
          </>
        }
      >
        <Stack direction={'column'}>
          <Stack
            direction={isDesktop ? 'row' : 'column'}
            gap={isDesktop ? '48px' : undefined}
          >
            <Stack
              direction='row'
              gap={isDesktop ? '45px' : '20px'}
              width='fit-content'
              flexWrap='wrap'
              justifyContent={isDesktop ? '' : 'space-between'}
            >
              <Stack
                direction={'column'}
                width={'fit-content'}
                maxWidth={'265px'}
              >
                <Typography variant={'body1'} width='275px'>
                  {paymentDeductionItem.part}
                </Typography>
                <GroupStatus
                  groupStatus={paymentDeductionItem.benefitGroupCode}
                  deductionType={
                    isDeduction &&
                    getPaymentDeductionType(
                      paymentDeductionItem,
                      Dictionary[DICTIONARY.PaymentsDeductions]
                        .paymentDeductionCard.deductionType
                    )
                  }
                />
              </Stack>
              <DataItem
                title={
                  isDeduction
                    ? dictionary.deductionAmount
                    : dictionary.paymentAmount
                }
                paymentAmount={paymentDeductionItem.appMaanakSum || false}
              />

              {paymentDeductionItem.payStartDate && (
                <Stack
                  direction='column'
                  justifyContent='center'
                  width={isDesktop ? 'fit-content' : '45%'}
                  minWidth='90px'
                  gap={isDesktop ? '11px' : '4px'}
                  height='fit-content'
                >
                  <Typography variant={'body6'}>
                    {isDeduction
                      ? dictionary.deductionDate
                      : dictionary.paymentDate}
                  </Typography>

                  <Typography variant={'body2'}>
                    {format(
                      new Date(paymentDeductionItem.payStartDate),
                      shorterDateFormat
                    )}
                    {paymentDeductionItem.payEndDate
                      ? ` ${dictionary.until} ${format(
                          new Date(paymentDeductionItem.payEndDate),
                          shorterDateFormat
                        )}`
                      : ''}
                  </Typography>
                </Stack>
              )}
              <DataItem
                title={dictionary.requestNumber}
                requestNumber={paymentDeductionItem.requestNumber}
              />
              <DataItem
                title={dictionary.treatmentMode}
                status={paymentDeductionItem.statusCode}
              />
            </Stack>
          </Stack>

          {(paymentDeductionItem.linkedApplyNumbers.length > 0 ||
            (paymentDeductionItem.payStartDate &&
              paymentDeductionItem.payEndDate)) && (
            <>
              <Divider
                sx={{
                  margin: '15px 0 10px 0',
                  borderColor: 'primary.shade_100',
                }}
                aria-hidden='true'
              />

              {paymentDeductionItem.linkedApplyNumbers.length > 0 && (
                <LinkedAppliedNumbers
                  appliedNumbers={paymentDeductionItem.linkedApplyNumbers || []}
                />
              )}

              {paymentDeductionItem.payStartDate &&
                paymentDeductionItem.payEndDate && (
                  <TextWithIcon text={dictionary.importantToKnow} />
                )}
            </>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: isDesktop ? '32px' : '24px 16px',
          backgroundColor: 'primary.shade_50',
          borderRadius: '0 0 16px 16px',
        }}
      >
        <Stack
          direction={'row'}
          flexWrap={isDesktop ? 'nowrap' : 'wrap'}
          gap={isDesktop ? 8 : 2}
          sx={{
            '& > *': {
              width: isDesktop ? 'auto' : 'calc(50% - 8px)',
            },
          }}
        >
          <Stack direction={'column'} width={'fit-content'}>
            <Typography variant={isDesktop ? 'body6' : 'body7'} mb={1}>
              {dictionary.lastUpdateDate}
            </Typography>
            <Typography variant={isDesktop ? 'body2' : 'body5'}>
              {format(
                new Date(paymentDeductionItem.statusDate),
                shorterDateFormat
              )}
            </Typography>
          </Stack>
          {paymentDeductionItem.payFromDate && (
            <Stack direction={'column'} width={'fit-content'}>
              <Typography variant={isDesktop ? 'body6' : 'body7'} mb={1}>
                {isDeduction ? dictionary.deductionFor : dictionary.paymentFor}
              </Typography>
              <Typography variant={isDesktop ? 'body2' : 'body5'}>
                {format(
                  new Date(paymentDeductionItem.payFromDate),
                  shorterDateFormat
                )}
                {paymentDeductionItem.payUntilDate
                  ? ` ${dictionary.until} ${format(
                      new Date(paymentDeductionItem.payUntilDate),
                      shorterDateFormat
                    )}`
                  : ''}
              </Typography>
            </Stack>
          )}
          {paymentDeductionItem.paymentDurationInMonths && (
            <Stack direction={'column'} width={'fit-content'}>
              <Typography variant={isDesktop ? 'body6' : 'body7'} mb={1}>
                {dictionary.numberOfMonths}
              </Typography>
              <Typography variant={isDesktop ? 'body2' : 'body5'}>
                {paymentDeductionItem.paymentDurationInMonths}
              </Typography>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
